<template>
  <div class="grid grid-cols-4 sm:grid-cols-4 grid-rows-1 gap-4">
    <DataSectionItem
      v-for="metric in dashboardMetrics"
      :key="metric.id"
      :title="metric.title"
      :value="metric.value"
      :change-text="metric.changeText"
      :up="metric.changeDirection === 'up'"
      :icon-background="metric.iconBackground"
      :icon-color="metric.iconColor"
      :title-only="metric.titleOnly || false"
    >
      <template #icon>
        <VaIcon :name="metric.icon" size="large" />
      </template>
    </DataSectionItem>
    <!-- YearlyBreakup class="h-full" / -->
  </div>
</template>

<script lang="ts" setup>
import { getChartData, useChartData } from '../../../../data/charts/composables/useChartData'
import { ChartOptions } from 'chart.js'
import { computed, onMounted, Ref, ref, watch } from 'vue'
import { OperatedViaje, ViajeCancelado } from '../../../../services/api-services/ViajesServices'
import dataStorageManager, { BucketDefaultIDs } from '../../../../managers/data/DataStorageManager'
import { TLineChartData } from '../../../../data/types'
import { IMarca, MarcasController } from '../../../../services/api-services/MarcasServices'
import { useColors } from 'vuestic-ui'
import DataSectionItem from './DataSectionItem.vue'
import dataHubServices from '../../../../services/data-hub-services/DataHubServices'
import { viajesHubName, ViajesHubPipelines } from '../../../../services/data-hub-services/ViajesHub'

interface DashboardMetric {
  id: string
  title: string
  value: string
  icon: string
  changeText: string
  changeDirection: 'up' | 'down'
  iconBackground: string
  iconColor: string
  titleOnly?: boolean // Optional property to indicate if the metric should not show change text
}

const dayEventos: Ref<{ dia: Date; texto: string; events: number }[]> = ref([])
const monthEventos: Ref<{ mes: Date; texto: string; events: number }[]> = ref([])

const { getColor } = useColors()

const pasajerosData: Ref<DashboardMetric> = ref({
  id: 'pasajeros',
  title: 'Promedio marcas mensuales',
  value: '61',
  icon: 'mso-account_circle',
  changeText: '5 marcas',
  changeDirection: 'up',
  iconBackground: getColor('success'),
  iconColor: getColor('on-success'),
}) // Conteo de pasajeros diarios promedio

const promedioViajesDiariosData: Ref<DashboardMetric> = ref({
  id: 'pviajesdiarios',
  title: 'Promedio viajes diarios',
  value: '14',
  icon: 'mso-grade',
  changeText: '4%',
  changeDirection: 'up',
  iconBackground: getColor('warning'),
  iconColor: getColor('on-warning'),
  titleOnly: true, // This metric does not show change text
}) // Conteo de nuevos viajes pendientes. Viajes sin posicionamientos

const tiempoPromedioEstadiaData: Ref<DashboardMetric> = ref({
  id: 'tpromestadia',
  title: 'Tiempo promedio de estadía',
  value: '3.2 horas',
  icon: 'mso-timer',
  changeText: '0.5 horas',
  changeDirection: 'down',
  iconBackground: getColor('danger'),
  iconColor: getColor('on-danger'),
}) // Tiempo entre posicionamiento e inicio + tiempo entre fin y salida

const tiempoPromedioViajeData: Ref<DashboardMetric> = ref({
  id: 'tpromviaje',
  title: 'Tiempo promedio de viaje',
  value: '1.2 horas',
  icon: 'mso-timer',
  changeText: '0.2 horas',
  changeDirection: 'up',
  iconBackground: getColor('info'),
  iconColor: getColor('on-info'),
}) // Tiempo entre inicio y fin del viaje

const dashboardMetrics: Ref<DashboardMetric[]> = computed(() => [
  pasajerosData.value,
  promedioViajesDiariosData.value,
  tiempoPromedioEstadiaData.value,
  tiempoPromedioViajeData.value,
])

const marcasController = new MarcasController()
const marcasList: Ref<Array<IMarca>> = ref([])

const showList: Ref<
  {
    viaje: OperatedViaje
    day: Date // Es el dia exacto del viaje
    month: Date // El primer dia del mes del viaje
  }[]
> = ref([])

const fullViajesList: Ref<OperatedViaje[]> = ref([])

onMounted(() => {
  dataHubServices.subscribeToHub(viajesHubName, {
    subscriberID: 'adminDataSection_viajes',
    pipeline: ViajesHubPipelines.VIAJES_WITH_TRIPULANTES,
    callback: (data: OperatedViaje[]) => {
      fullViajesList.value = data
    },
  })
})

watch(fullViajesList, () => {
  const showListTemp: {
    viaje: OperatedViaje
    day: Date
    month: Date
  }[] = []
  fullViajesList.value.forEach((viaje) => {
    if (viaje.posicionamientosData.length > 0 && viaje.estado !== null && !ViajeCancelado(viaje)) {
      const viajeDate = new Date(viaje.fecha_viaje)
      const day = new Date(viajeDate.getFullYear(), viajeDate.getMonth(), viajeDate.getDate())
      const month = new Date(viajeDate.getFullYear(), viajeDate.getMonth(), 1)
      showListTemp.push({
        viaje: viaje,
        day: day,
        month: month,
      })
    }
  })
  showList.value = showListTemp
})

watch(showList, async () => {
  // Fecha de inicio, fecha mas antigua de los meses
  const fechaInicio = showList.value.reduce((oldest, item) => {
    return item.month < oldest ? item.month : oldest
  }, new Date())
  // Fecha de fin, fecha mas reciente de los dias
  const fechaFin = showList.value.reduce((newest, item) => {
    return item.day > newest ? item.day : newest
  }, new Date(0))
  fechaFin.setHours(23, 59, 59, 999)

  await marcasController.loadData(fechaInicio, fechaFin, 1000 * 30) // Recargar cada 30 segundos mientras se tengan datos
  marcasList.value = marcasController.getMarcasData()

  const daySplittedSet = new Set<{ dia: Date; cant: number }>()
  const monthSplittedSet = new Set<{ mes: Date; cant: number }>()
  showList.value.forEach((item) => {
    const dayEntry = Array.from(daySplittedSet).find((d) => d.dia.getTime() === item.day.getTime())
    const dayMarks = marcasList.value.filter((m) => {
      const marcaDate = new Date(m.record_time)
      return (
        marcaDate.getFullYear() === item.day.getFullYear() &&
        marcaDate.getMonth() === item.day.getMonth() &&
        marcaDate.getDate() === item.day.getDate()
      )
    })
    if (!dayEntry) {
      daySplittedSet.add({ dia: item.day, cant: dayMarks.length })
    }
    const monthEntry = Array.from(monthSplittedSet).find((m) => m.mes.getTime() === item.month.getTime())
    const monthMarks = marcasList.value.filter((m) => {
      const marcaDate = new Date(m.record_time)
      return marcaDate.getFullYear() === item.month.getFullYear() && marcaDate.getMonth() === item.month.getMonth()
    })
    if (!monthEntry) {
      monthSplittedSet.add({ mes: item.month, cant: monthMarks.length })
    }
  })
  dayEventos.value = Array.from(daySplittedSet)
    .sort((a, b) => a.dia.getTime() - b.dia.getTime())
    .map((d) => ({ dia: d.dia, texto: `${d.cant} viajes`, events: d.cant }))
  monthEventos.value = Array.from(monthSplittedSet)
    .sort((a, b) => a.mes.getTime() - b.mes.getTime())
    .map((m) => ({ mes: m.mes, texto: `${m.cant} viajes`, events: m.cant }))

  const currentMonth = new Date().getMonth()
  const currentYear = new Date().getFullYear()

  const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1
  const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear

  // Update pasajerosData metric
  const currentMonthEvents = monthEventos.value.filter(
    (m) => m.mes.getFullYear() === currentYear && m.mes.getMonth() === currentMonth,
  )
  const currentMonthDays = new Date(currentYear, currentMonth + 1, 0).getDate() // Cantidad de dias que han pasado del mes
  const previousMonthEvents = monthEventos.value.filter(
    (m) => m.mes.getFullYear() === previousYear && m.mes.getMonth() === previousMonth,
  )
  const previousMonthDays = 30 // Cantidad de dias que pasaron el mes anterior

  const currentMonthAverage =
    currentMonthEvents.length > 0 ? currentMonthEvents.reduce((acc, val) => acc + val.events, 0) / currentMonthDays : 0
  const previousMonthAverage =
    previousMonthEvents.length > 0
      ? previousMonthEvents.reduce((acc, val) => acc + val.events, 0) / previousMonthDays
      : 0

  pasajerosData.value.value = currentMonthAverage.toFixed(0)
  // Calculate changeText
  if (currentMonthEvents && previousMonthEvents) {
    const diff = currentMonthAverage - previousMonthAverage
    const changeDiference = Math.abs(diff).toFixed(0)
    pasajerosData.value.changeText = `${changeDiference} marcas`
    pasajerosData.value.changeDirection = diff >= 0 ? 'up' : 'down'

    /*const changePercent = previousMonthEvents.events > 0 ? (diff / previousMonthEvents.events) * 100 : 0
    pasajerosData.value.changeText = `${Math.abs(changePercent).toFixed(2)}%`
    pasajerosData.value.changeDirection = diff >= 0 ? 'up' : 'down'*/
  } else {
    pasajerosData.value.changeText = '0'
    pasajerosData.value.changeDirection = 'up'
  }

  const averageTripsPerDay =
    showList.value.length > 0
      ? showList.value.filter(
          (v) =>
            new Date(v.viaje.fecha_viaje).getFullYear() === currentYear &&
            new Date(v.viaje.fecha_viaje).getMonth() === currentMonth,
        ).length / currentMonthDays
      : 0
  promedioViajesDiariosData.value.value = averageTripsPerDay.toFixed(0)

  /*const pendingTrips = fullViajesList.value.filter(
    (v) => v.posicionamientosData.length === 0 && v.estado == null,
  ).length
  viajesPendientesData.value.value = pendingTrips.toString()*/

  // Update tiempoPromedioEstadiaData metric
  const completedTrips = fullViajesList.value
    .filter((v) => v.estado !== null && !ViajeCancelado(v))
    .filter((v) => {
      const viajeDate = new Date(v.fecha_viaje)
      return viajeDate.getFullYear() === currentYear && viajeDate.getMonth() === currentMonth
    })
  const previousTrips = fullViajesList.value
    .filter((v) => v.estado !== null && !ViajeCancelado(v))
    .filter((v) => {
      const viajeDate = new Date(v.fecha_viaje)
      return viajeDate.getFullYear() === previousYear && viajeDate.getMonth() === previousMonth
    })
  let totalEstadiaMinutes = 0
  completedTrips.forEach((v) => {
    const posicionamiento = v.posicionamientosData.sort(
      (a, b) => new Date(a.fecha_solicitud).getTime() - new Date(b.fecha_solicitud).getTime(),
    )[0]
    if (posicionamiento && v.marca_fecha_inicio && v.marca_fecha_fin && v.marca_salida) {
      const inicio = new Date(v.marca_fecha_inicio).getTime()
      const fin = new Date(v.marca_fecha_fin).getTime()
      const salida = new Date(v.marca_salida).getTime()
      const tiempoViaje = (fin - inicio) / (1000 * 60) // in minutes
      const tiempoEstadia = (salida - fin) / (1000 * 60) // in minutes
      totalEstadiaMinutes += tiempoViaje + tiempoEstadia
    }
  })
  let prevEstadiaMinutes = 0
  previousTrips.forEach((v) => {
    const posicionamiento = v.posicionamientosData.sort(
      (a, b) => new Date(a.fecha_solicitud).getTime() - new Date(b.fecha_solicitud).getTime(),
    )[0]
    if (posicionamiento && v.marca_fecha_inicio && v.marca_fecha_fin && v.marca_salida) {
      const inicio = new Date(v.marca_fecha_inicio).getTime()
      const fin = new Date(v.marca_fecha_fin).getTime()
      const salida = new Date(v.marca_salida).getTime()
      const tiempoViaje = (fin - inicio) / (1000 * 60) // in minutes
      const tiempoEstadia = (salida - fin) / (1000 * 60) // in minutes
      prevEstadiaMinutes += tiempoViaje + tiempoEstadia
    }
  })

  const avgEstadiaMinutes = completedTrips.length > 0 ? totalEstadiaMinutes / completedTrips.length : 0
  const avgEstadiaHours = Math.floor(avgEstadiaMinutes / 60)
  const remainingMinutes = Math.round(avgEstadiaMinutes % 60)
  tiempoPromedioEstadiaData.value.value = `${avgEstadiaHours} horas ${remainingMinutes} minutos`

  // Calculate previous average for changeText
  const prevAvgEstadiaMinutes = previousTrips.length > 0 ? prevEstadiaMinutes / previousTrips.length : 0
  const diffEstadiaMinutes = avgEstadiaMinutes - prevAvgEstadiaMinutes
  const diffEstadiaHours = Math.floor(Math.abs(diffEstadiaMinutes) / 60)
  const diffEstadiaRemainingMinutes = Math.round(Math.abs(diffEstadiaMinutes) % 60)
  tiempoPromedioEstadiaData.value.changeText = `${diffEstadiaHours} horas ${diffEstadiaRemainingMinutes} minutos`
  tiempoPromedioEstadiaData.value.changeDirection = diffEstadiaMinutes <= 0 ? 'down' : 'up'

  // Update tiempoPromedioViajeData metric
  let totalViajeMinutes = 0
  completedTrips.forEach((v) => {
    if (v.marca_fecha_inicio && v.marca_fecha_fin) {
      const inicio = new Date(v.marca_fecha_inicio).getTime()
      const fin = new Date(v.marca_fecha_fin).getTime()
      const tiempoViaje = (fin - inicio) / (1000 * 60) // in minutes
      totalViajeMinutes += tiempoViaje
    }
  })
  let prevViajeMinutes = 0
  previousTrips.forEach((v) => {
    if (v.marca_fecha_inicio && v.marca_fecha_fin) {
      const inicio = new Date(v.marca_fecha_inicio).getTime()
      const fin = new Date(v.marca_fecha_fin).getTime()
      const tiempoViaje = (fin - inicio) / (1000 * 60) // in minutes
      prevViajeMinutes += tiempoViaje
    }
  })
  const avgViajeMinutes = completedTrips.length > 0 ? totalViajeMinutes / completedTrips.length : 0
  const avgViajeHours = Math.floor(avgViajeMinutes / 60)
  const avgViajeRemainingMinutes = Math.round(avgViajeMinutes % 60)
  tiempoPromedioViajeData.value.value = `${avgViajeHours} horas ${avgViajeRemainingMinutes} minutos`

  // Calculate previous average for changeText
  const prevAvgViajeMinutes = previousTrips.length > 0 ? prevViajeMinutes / previousTrips.length : 0
  const diffViajeMinutes = avgViajeMinutes - prevAvgViajeMinutes
  const diffViajeHours = Math.floor(Math.abs(diffViajeMinutes) / 60)
  const diffViajeRemainingMinutes = Math.round(Math.abs(diffViajeMinutes) % 60)
  tiempoPromedioViajeData.value.changeText = `${diffViajeHours} horas ${diffViajeRemainingMinutes} minutos`
  tiempoPromedioViajeData.value.changeDirection = diffViajeMinutes <= 0 ? 'down' : 'up'
})
</script>
<style scoped></style>
